import i18n from "i18next";

import {getRestrictedValueColor} from "../facets/ColorUtils";
import {AttributeType, isAttributeTypeString} from "../schema/Attribute";
import {DtConstants} from "../schema/DtConstants";

const { StreamStates } = DtConstants;

export class IotReadings {
	instant;
	filter;
	last;
	streamMgr;

	constructor(instant, last, streamMgr, filter) {
		this.instant = instant;
		this.last = last;
		this.streamMgr = streamMgr;
		this.filter = filter;
	}

	/** Shallow clone to satisfy React */
	clone() {
		return new IotReadings(this.instant, this.last, this.streamMgr, this.filter);
	}

	get(dbId, attrId) {
		const stream = this.streamMgr.streamsCache.get(dbId);
		if (!stream) return;

		const attr = stream.streamAttrs.find(({ id }) => id === attrId);
		if (!attr) return;

		return new IotReading(
			stream,
			attr,
			this.last?.get(stream.dbId)?.[attr.id],
			this.instant?.get(stream.dbId)?.[attr.id],
			this.streamMgr
		);
	}
}

/**
 * Reading interface to data (maybe lazy?)
 *
 * Goal is to get a few basic data bits in, and to have them also have functionalities linked.
 */
export class IotReading {
	constructor(stream, attr, last, instant, streamMgr) {
		this.stream = stream;
		this.attr = attr;
		this.last = last && toArray(last);
		this.instant = instant;
		this.streamMgr = streamMgr;

		// we could eventually cache lazily computed values, but leaving this for later
		// esp. since undefined doesn't mean "not computed yet".
	}

	/** Is there any data for this reading. */
	exists() { return !!this.last; }

	/** Get colour for a discrete reading. */
	getColorFor(value = this.valueOf()) {
		const { displayOptions, allowedValues, dataType } = this.attr;

		const indexedValues = allowedValues?.list;
		const allowedList = indexedValues ? indexedValues.slice(1) : []; // Skip the first value, which is indexed to undefined values
		return getRestrictedValueColor(dataType, allowedList, displayOptions, value);
	}

	/** Get what data point applies at this time. (at least [ts, value]) */
	getDataPoint() {
		// If there's a value for the current time
		if (this.instant) return this.instant;

		// A stream can't be OFFLINE if historical (ie. requested is more than offlineTimeout ago)
		return this.isHistorical() ? undefined : this.last;
	}

	/**
	 * Get the stream state associated with the data point at this time.
	 * https://wiki.autodesk.com/display/TANDEM/Time+Series
	 */
	getState() {
		// Value for the current time exists (it can come from up to offline age before timeline's time) (carry forward)
		if (this.instant) {
			const thresholds = this.getThresholds();
			if (thresholds) {
				return this.instant[2] || StreamStates.Normal;
			} else {
				// Normal / Compliant but not "within thresholds"
				return null;
			}
		}

		// If we ever received a value for the stream, check whether we should mention it.
		if (this.last) {
			if (this.isHistorical()) {
				// No data / historical reading
				return StreamStates.NoData;
			} else {
				return StreamStates.Offline;
			}
		} else {
			// No data / Never configured.
			return StreamStates.NoData;
		}
	}

	/** Get thresholds that applies to this reading. */
	getThresholds() {
		return this.attr.allowedValues?.thresholds;
	}

	/** Is the reading type discrete. */
	isDiscrete() { return isAttributeTypeString(this.attr.dataType); }

	/** Whether reading is discrete, and represent an un-mapped code. */
	isUnmappedCode() {
		const value = this.valueOf();
		return value != null && this.isDiscrete() && this.attr.allowedValues.list[value] === undefined;
	}

	/** Value of the reading at this time. */
	valueOf() {
		return this.getDataPoint()?.[1];
	}

	/** Default formatting for the reading's value at this time. */
	toString() {
		const value = this.valueOf();
		if (!value) return;

		const formatted = this.streamMgr.formatStreamValue(value, this.attr);
		switch (this.attr.dataType) {
			case AttributeType.String:
				return formatted === undefined ? i18n.t('(Undefined)') : formatted;
			default:
				return formatted;
		}
	}

	/**
	 * Historical readings are readings for a selected time that is older than the stream's offline timeout ago.
	 *
	 * They are important as we decided that we wouldn't try to determine the offline state of a stream, if historical
	 * given it would lead to costly data queries.
	 *
	 * If
	 *     time is historical for this stream, only NoData or a value are possible.
	 * or else
	 *     if no value for the current time, but ever received a value, we say it was offline at the selected time.
	 * @see getState()
	 * @private
	 */
	isHistorical() {
		return this.streamMgr.view.time.ago() > this.stream.settings.offlineTimeout * 1000;
	}
}

// Converts from last readings { [ts]: val } to [ts, value] structure.
function toArray(lr) {
	for (const ts in lr) {
		return [Number(ts), lr[ts]];
	}
}
